import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useIsAdmin } from "@/hooks/useIsAdmin";

import { getPaymentsReadiness } from "@/lib/payments-readiness.functions";
import type { ReadinessReport } from "@/lib/payments-readiness.server";

type StripeEnv = "sandbox" | "live";

const clientToken = import.meta.env["VITE_PAYMENTS_CLIENT_TOKEN"] as string | undefined;

function detectEnvironment(): StripeEnv | null {
  if (clientToken?.startsWith("pk_live_")) return "live";
  if (clientToken?.startsWith("pk_test_")) return "sandbox";
  return null;
}

/**
 * Polls Stripe every 15s until every readiness check passes, then stops.
 * Refetches on window focus so the app updates the moment go-live lands.
 */
export function usePaymentsReadiness() {
  const environment = detectEnvironment();
  // The readiness report is admin-only server-side; only admins may query it.
  const { isAdmin } = useIsAdmin();
  // Deferred: this hits the Stripe API server-side and runs on every page,
  // so it must not compete with the initial page load.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setArmed(true), 2500);
    return () => clearTimeout(id);
  }, []);

  const query = useQuery<ReadinessReport>({
    queryKey: ["payments-readiness", environment],
    enabled: !!environment && armed && isAdmin,
    queryFn: () => getPaymentsReadiness({ data: { environment: environment as StripeEnv } }),
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (q.state.data?.ready ? false : 15_000),
    staleTime: 10_000,
    retry: 1,
  });

  return {
    ...query,
    environment,
    report: query.data ?? null,
    ready: query.data?.ready ?? false,
    failures: (query.data?.checks ?? []).filter((c) => c.status === "fail"),
  };
}
